<template>
  <div
    v-if="active"
    :key="burstKey"
    class="confetti-burst"
    :class="{ 'confetti-burst--static': reduced, 'confetti-burst--loop': loop }"
    :style="{ '--cb-confetti-duration': `${duration}ms` }"
    aria-hidden="true"
  >
    <span
      v-for="piece in pieces"
      :key="piece.id"
      class="confetti-burst__piece"
      :class="`confetti-burst__piece--${piece.shape}`"
      :style="piece.style"
    ></span>
  </div>
</template>

<script setup>
/**
 * ConfettiBurst — декоративный взрыв лаймово-жёлтого конфетти из центра контейнера (CSS-анимация).
 * Родитель должен быть position: relative; компонент занимает его целиком и не ловит клики.
 *
 * <div style="position:relative"><ConfettiBurst /> …</div>          // один взрыв при монтировании
 * <ConfettiBurst loop :count="36" :spread="220" />                   // повторять каждые duration мс
 * <ConfettiBurst ref="c" :active="false" /> … c.value.fire()         // запуск вручную
 *
 * props: count (28), spread (px, 180), duration (мс, 1800), loop, active (true), seed.
 * expose: fire() — перезапустить взрыв.
 * prefers-reduced-motion: кусочки рисуются статично вокруг центра, без анимации.
 */
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { prefersReducedMotion } from "@/utils/format";

const props = defineProps({
  count: { type: Number, default: 28 },
  spread: { type: Number, default: 180 },
  duration: { type: Number, default: 1800 },
  loop: { type: Boolean, default: false },
  active: { type: Boolean, default: true },
  seed: { type: Number, default: 7 },
});

const COLORS = ["#DDF55A", "#F2FA8C", "#C9E64A", "#F6E05E", "#FFF7A8", "#B8D93A"];
const SHAPES = ["rect", "rect", "strip", "dot"];

const reduced = ref(false);
const burstKey = ref(0);
let loopTimer = null;

/** Детерминированный ГПСЧ (mulberry32), чтобы раскладка была стабильной между рендерами. */
function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const pieces = computed(() => {
  const rand = rng(props.seed + burstKey.value * 101);
  return Array.from({ length: props.count }, (_, id) => {
    const angle = (id / props.count) * Math.PI * 2 + rand() * 0.6;
    const distance = props.spread * (0.45 + rand() * 0.55);
    const dx = Math.cos(angle) * distance;
    const dy = Math.sin(angle) * distance * 0.85;
    const size = 6 + Math.round(rand() * 8);
    return {
      id,
      shape: SHAPES[Math.floor(rand() * SHAPES.length)],
      style: {
        "--dx": `${dx.toFixed(1)}px`,
        "--dy": `${dy.toFixed(1)}px`,
        "--rot": `${Math.round(180 + rand() * 540)}deg`,
        "--size": `${size}px`,
        "--color": COLORS[Math.floor(rand() * COLORS.length)],
        animationDelay: `${Math.round(rand() * 160)}ms`,
        // для статичного режима
        "--static-transform": `translate(-50%, -50%) translate(${(dx * 0.8).toFixed(1)}px, ${(dy * 0.8).toFixed(1)}px) rotate(${Math.round(rand() * 360)}deg)`,
      },
    };
  });
});

/** Перезапустить взрыв. */
function fire() {
  burstKey.value += 1;
}

onMounted(() => {
  reduced.value = prefersReducedMotion();
  if (props.loop && !reduced.value) {
    loopTimer = setInterval(fire, props.duration + 600);
  }
});

onBeforeUnmount(() => clearInterval(loopTimer));

defineExpose({ fire });
</script>

<style scoped>
.confetti-burst {
  position: absolute;
  inset: 0;
  overflow: visible;
  pointer-events: none;
  z-index: 0;
}

.confetti-burst__piece {
  position: absolute;
  top: 50%;
  left: 50%;
  width: var(--size);
  height: calc(var(--size) * 0.55);
  background: var(--color);
  border-radius: 2px;
  box-shadow: 0 0 8px rgba(221, 245, 90, 0.55);
  opacity: 0;
  animation: cb-confetti-burst var(--cb-confetti-duration) cubic-bezier(0.15, 0.7, 0.3, 1) forwards;
}

.confetti-burst__piece--strip {
  width: calc(var(--size) * 0.35);
  height: calc(var(--size) * 1.4);
}

.confetti-burst__piece--dot {
  width: calc(var(--size) * 0.6);
  height: calc(var(--size) * 0.6);
  border-radius: 50%;
}

.confetti-burst--static .confetti-burst__piece {
  animation: none;
  opacity: 0.85;
  transform: var(--static-transform);
}

@media (prefers-reduced-motion: reduce) {
  .confetti-burst__piece {
    animation: none !important;
    opacity: 0.85;
    transform: var(--static-transform);
  }
}
</style>
